class ColorSelector extends HTMLElement {
  constructor() {
    super();
    this.prefetchCache = new Map();
    this.prefetchInProgress = new Set();
    /** @type {Object<string, {url: string, hasWidthOption: boolean, options: Object[], variants: {id: number, available: boolean, options: string[]}[]}>|null} */
    this.allProductsVariants = null;
    /** @type {boolean} Flag to prevent re-running setup when element is re-inserted into DOM */
    this.hasInitialized = false;
    this.sectionRenderingColorSwapping = false;
  }

  /**
   * Loads preloaded variant data for all products/colors from the script tag.
   * This allows immediate variant lookup when switching colors.
   */
  loadAllProductsVariants() {
    if (this.allProductsVariants) return;
    
    const script = this.querySelector('script[data-all-products-variants]');
    if (!script?.textContent) return;
    
    try {
      this.allProductsVariants = JSON.parse(script.textContent);
    } catch (e) {
      console.error('Failed to parse all-products-variants data:', e);
    }
  }

  /**
   * Finds the variant ID for a product given size and width options using preloaded data.
   * @param {string} productHandle - The product handle to look up
   * @param {string|null} size - The size option value
   * @param {string|null} width - The width option value (optional)
   * @returns {{variantId: string, available: boolean, hasWidthOption: boolean}}
   */
  findVariantFromPreloadedData(productHandle, size, width) {
    /** @type {{variantId: string, available: boolean, hasWidthOption: boolean}} */
    const result = { variantId: '', available: false, hasWidthOption: false };
    
    if (!this.allProductsVariants || !size) return result;
    
    const productData = this.allProductsVariants[productHandle];
    if (!productData) return result;
    
    result.hasWidthOption = productData.hasWidthOption;
    
    // Find matching variant
    for (const variant of productData.variants) {
      const hasSize = variant.options.includes(size);
      
      if (productData.hasWidthOption && width) {
        // Product has width - need both to match
        const hasWidth = variant.options.includes(width);
        if (hasSize && hasWidth) {
          result.variantId = String(variant.id);
          result.available = variant.available;
          return result;
        }
      } else if (!productData.hasWidthOption) {
        // Product doesn't have width - just match size
        if (hasSize) {
          result.variantId = String(variant.id);
          result.available = variant.available;
          return result;
        }
      }
    }
    
    // If we have width but target product doesn't, try to find just by size
    if (width && !productData.hasWidthOption) {
      for (const variant of productData.variants) {
        if (variant.options.includes(size)) {
          result.variantId = String(variant.id);
          result.available = variant.available;
          return result;
        }
      }
    }
    
    // If target product has width but we don't have one saved, find first variant with this size
    if (!width && productData.hasWidthOption) {
      for (const variant of productData.variants) {
        if (variant.options.includes(size)) {
          result.variantId = String(variant.id);
          result.available = variant.available;
          return result;
        }
      }
    }
    
    return result;
  }

  /**
   * Captures the currently selected size and width from the variant picker.
   * @param {HTMLElement} productContainer - The product container element
   * @returns {{size: string|null, width: string|null}} The selected option values
   */
  captureSelectedOptions(productContainer) {
    const variantPicker = productContainer.querySelector('variant-picker');
    if (!variantPicker) return { size: null, width: null };

    let selectedSize = null;
    let selectedWidth = null;

    // Find size option (fieldset without width or swatch classes)
    const sizeFieldset = variantPicker.querySelector('fieldset.variant-option--buttons:not(.variant-option--width):not(.variant-option--swatches)');
    if (sizeFieldset) {
      /** @type {HTMLInputElement|null} */
      const checkedSize = sizeFieldset.querySelector('input:checked');
      if (checkedSize) {
        selectedSize = checkedSize.value;
      }
    }

    // Find width option
    const widthFieldset = variantPicker.querySelector('fieldset.variant-option--width');
    if (widthFieldset) {
      /** @type {HTMLInputElement|null} */
      const checkedWidth = widthFieldset.querySelector('input:checked');
      if (checkedWidth) {
        selectedWidth = checkedWidth.value;
      }
    }

    return { size: selectedSize, width: selectedWidth };
  }

  /**
   * Updates the URL with the variant ID after the product URL has been pushed.
   * @param {string} newVariantId - The variant ID to add to URL
   */
  updateUrlWithVariant(newVariantId) {
    // Update the URL with the new variant ID (after the product URL is pushed)
    setTimeout(() => {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', newVariantId);
      window.history.replaceState({}, '', url.toString());
    }, 0);
  }

  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    if (!this.sectionId) return;
    if (this.dataset.sectionRenderingColorSwapping === 'true'){
      this.sectionRenderingColorSwapping = true;
    }
    // Skip re-initialization if element was just re-inserted into DOM
    // (e.g., after variant-picker DOM update that preserves this element)
    if (this.hasInitialized) return;
    this.hasInitialized = true;

    this.loadAllProductsVariants();
    this.setupPrefetch();
    this.setupEventListeners();
    this.setupNavigationHandling();
      window?.yotpoWidgetsContainer?.initWidgets();
  }

  /**
   * Updates all Yotpo review widget attributes when product changes
   * @param {Document} newDocument - Parsed HTML document from new product page
   * @param {HTMLElement} productContainer - Product container element
   */
  updateYotpoReviewWidget(newDocument, productContainer) {
    if (!productContainer?.querySelector('color-selector')) return;

    const newColorSelector = newDocument?.querySelector('color-selector');
    const yotpoReviewWidget = document?.querySelector('[data-v-app]');
    if(!yotpoReviewWidget && !newColorSelector) return;

    // Update all Yotpo data attributes from the new product
    const yotpoAttributes = {
      'yotpoProductId': newColorSelector.dataset.productId,
      'yotpoName': newColorSelector.dataset.productName,
      'yotpoUrl': newColorSelector.dataset.productUrl,
      'yotpoPrice': newColorSelector.dataset.productPrice,
      'yotpoCurrency': newColorSelector.dataset.productCurrency,
      'yotpoImageUrl': newColorSelector.dataset.productImage,
      'yotpoDescription': newColorSelector.dataset.productDescription
    };

    // Apply all attributes to the widget
    Object.entries(yotpoAttributes).forEach(([key, value]) => {
      if (value) {
        yotpoReviewWidget.dataset[key] = value;
      }
    });

    // Reinitialize Yotpo widgets with new product data
    if (typeof yotpoWidgetsContainer !== 'undefined' && yotpoWidgetsContainer) {
      yotpoWidgetsContainer.initWidgets();
    }
  }

  prefetchVariant(url) {
    if (this.prefetchCache.has(url) || this.prefetchInProgress.has(url)) return;
    
    this.prefetchInProgress.add(url);
    fetch(url)
      .then(response => response.text())
      .then(html => {
        this.prefetchCache.set(url, html);
        this.prefetchInProgress.delete(url);
      })
      .catch(e => {
        this.prefetchInProgress.delete(url);
      });
  }

  setupPrefetch() {
    // Delayed prefetch: wait for idle time, then prefetch all variants
    // Uses preloaded data to include variant parameter for accurate prefetching
    const prefetchAll = () => {
      const productContainer = this.closest('[data-product-information-content]');
      const savedOptions = productContainer 
        ? this.captureSelectedOptions(/** @type {HTMLElement} */ (productContainer))
        : { size: null, width: null };

      const swatches = this.querySelectorAll('.color-selector__swatch:not(.is-active)');
      swatches.forEach(el => {
        const swatch = /** @type {HTMLElement} */ (el);
        const productUrl = swatch.dataset.productUrl;
        const productHandle = swatch.dataset.productHandle;
        if (!productUrl) return;

        // Use preloaded data to find variant ID for prefetch URL
        let url = `${productUrl}?section_id=${this.sectionId}`;
        if (productHandle && savedOptions.size) {
          const preloadedResult = this.findVariantFromPreloadedData(productHandle, savedOptions.size, savedOptions.width);
          if (preloadedResult.variantId) {
            url += `&variant=${preloadedResult.variantId}`;
          }
        }
        this.prefetchVariant(url);
      });
    };

    // Use requestIdleCallback for better performance, fallback to setTimeout
    if ('requestIdleCallback' in window) {
      requestIdleCallback(prefetchAll, { timeout: 2000 });
    } else {
      setTimeout(prefetchAll, 1500);
    }
  }

  setupNavigationHandling() {
    // Handle browser back/forward navigation
    this.handlePopstate = (event) => {
      const currentUrl = window.location.href;
      const url = `${currentUrl}?section_id=${this.sectionId}`;
      
      const productContainer = this.closest('[data-product-information-content]');
      if (!productContainer) return;

      productContainer.classList.add('loading');

      // Use cached data if available, otherwise fetch
      const dataPromise = this.prefetchCache.has(url) 
        ? Promise.resolve(this.prefetchCache.get(url))
        : fetch(url).then(response => response.text());

      dataPromise
        .then(html => {
          const newDocument = new DOMParser().parseFromString(html, 'text/html');
          
          const baseSelectors = [
            '[data-product-top-header]',
            '[data-product-media-gallery]',
            '[data-product-details]'
          ];

          const dynamicUpdateBlocks = productContainer.querySelectorAll('[data-product-update-block]');
          const dynamicSelectors = Array.from(dynamicUpdateBlocks).map(el => 
            `[data-product-update-block="${el.getAttribute('data-product-update-block')}"]`
          );

          const allSelectors = [...baseSelectors, ...dynamicSelectors];

          const updateElement = (selector) => {
            const currentElement = productContainer.querySelector(selector);
            const newElement = newDocument.querySelector(selector);
            
            if (currentElement && newElement) {
              // Direct DOM swap without view transition to prevent flash of incorrect state
              currentElement.innerHTML = newElement.innerHTML;
            }
          };

          allSelectors.forEach(selector => updateElement(selector));
          this.updateYotpoReviewWidget(newDocument, productContainer)
        })
        .catch(e => {
          console.error('Error updating product on navigation:', e);
        })
        .finally(() => {
          productContainer.classList.remove('loading');
        });
    };

    window.addEventListener('popstate', this.handlePopstate);
  }

  disconnectedCallback() {
    // Clean up event listeners
    if (this.handlePopstate) {
      window.removeEventListener('popstate', this.handlePopstate);
    }
  }

  setupEventListeners() {
    // Prefetch on hover as fallback for early interactions
    // Uses preloaded data to include variant parameter for accurate prefetching
    this.addEventListener('mouseover', (event) => {
      const swatch = /** @type {HTMLElement|null} */ (event.target.closest('.color-selector__swatch'));
      if (!swatch || swatch.classList.contains('is-active')) return;
      
      const productUrl = swatch.dataset.productUrl;
      const productHandle = swatch.dataset.productHandle;
      if (!productUrl) return;

      // Get current selection for variant-aware prefetching
      const productContainer = this.closest('[data-product-information-content]');
      const savedOptions = productContainer 
        ? this.captureSelectedOptions(/** @type {HTMLElement} */ (productContainer))
        : { size: null, width: null };

      let url = `${productUrl}?section_id=${this.sectionId}`;
      if (productHandle && savedOptions.size) {
        const preloadedResult = this.findVariantFromPreloadedData(productHandle, savedOptions.size, savedOptions.width);
        if (preloadedResult.variantId) {
          url += `&variant=${preloadedResult.variantId}`;
        }
      }
      this.prefetchVariant(url);
    });

    // Handle click to switch products
    this.addEventListener('click', (event) => {
      if (!this.sectionRenderingColorSwapping) return;
      const swatch = event.target.closest('.color-selector__swatch');
      if (!swatch) return;
      event.preventDefault();

      const productUrl = swatch.dataset.productUrl;
      const productHandle = swatch.dataset.productHandle;
      if (!productUrl) return;

      const productContainer = this.closest('[data-product-information-content]');
      if (!productContainer) return;

      // Capture selected size and width BEFORE switching
      const savedOptions = this.captureSelectedOptions(/** @type {HTMLElement} */ (productContainer));

      // Use preloaded data to find the variant ID for the target product
      // Liquid will render with correct state - no post-fetch manipulation needed
      const preloadedResult = productHandle 
        ? this.findVariantFromPreloadedData(productHandle, savedOptions.size, savedOptions.width)
        : { variantId: '', available: false, hasWidthOption: false };

      // Build URL with variant parameter - Liquid handles all state
      let url = `${productUrl}?section_id=${this.sectionId}`;
      if (preloadedResult.variantId) {
        url += `&variant=${preloadedResult.variantId}`;
      }

      // Capture current product ID before switching
      const previousProductId = this.dataset.productId;

      productContainer.classList.add('loading');

      // Single fetch with variant parameter - Liquid renders correct state
      const cacheKey = url;
      const dataPromise = this.prefetchCache.has(cacheKey) 
        ? Promise.resolve(this.prefetchCache.get(cacheKey))
        : fetch(url).then(response => response.text());

      dataPromise
        .then(html => {
          const newDocument = new DOMParser().parseFromString(html, 'text/html');
          
          const selectors = [
            '[data-product-top-header]',
            '[data-product-media-gallery]',
            '[data-product-details]'
          ];

          // Direct DOM swap - Liquid already rendered correct state
          selectors.forEach(selector => {
            const currentElement = productContainer.querySelector(selector);
            const newElement = newDocument.querySelector(selector);
            if (currentElement && newElement) {
              currentElement.innerHTML = newElement.innerHTML;
            }
          });

          this.updateYotpoReviewWidget(newDocument, productContainer);
          
          // Get new product ID from the new document
          const newColorSelector = /** @type {HTMLElement | null} */ (newDocument.querySelector('color-selector'));
          const newProductId = newColorSelector?.dataset?.productId;
          
          // Update browser URL with product and variant
          window.history.pushState({}, '', productUrl);
          if (preloadedResult.variantId) {
            this.updateUrlWithVariant(preloadedResult.variantId);
          }
          
          // Dispatch color-change event for other components to re-initialize
          this.dispatchEvent(new CustomEvent('color-change', {
            bubbles: true,
            detail: {
              previousProductId,
              newProductId,
              savedOptions,
              newVariantId: preloadedResult.variantId,
              variantAvailable: preloadedResult.available,
              productUrl
            }
          }));
        })
        .catch(e => {
          console.error('Error updating product:', e);
        })
        .finally(() => {
          productContainer.classList.remove('loading');
        });
    });
  }
}

customElements.define('color-selector', ColorSelector);
